import { createContext } from "react";
import { decodeSanityNodeData } from "@sanity/visual-editing-csm";
import { VERCEL_STEGA_REGEX, vercelStegaDecode } from "@vercel/stega";
const byteToHex = [];
for (let i = 0; i < 256; ++i) byteToHex.push((i + 256).toString(16).slice(1));
function unsafeStringify(arr, offset = 0) {
	return (byteToHex[arr[offset + 0]] + byteToHex[arr[offset + 1]] + byteToHex[arr[offset + 2]] + byteToHex[arr[offset + 3]] + "-" + byteToHex[arr[offset + 4]] + byteToHex[arr[offset + 5]] + "-" + byteToHex[arr[offset + 6]] + byteToHex[arr[offset + 7]] + "-" + byteToHex[arr[offset + 8]] + byteToHex[arr[offset + 9]] + "-" + byteToHex[arr[offset + 10]] + byteToHex[arr[offset + 11]] + byteToHex[arr[offset + 12]] + byteToHex[arr[offset + 13]] + byteToHex[arr[offset + 14]] + byteToHex[arr[offset + 15]]).toLowerCase();
}
const rnds8 = /* @__PURE__ */ new Uint8Array(16);
function rng() {
	return crypto.getRandomValues(rnds8);
}
function v4(options, buf, offset) {
	return !buf && !options && crypto.randomUUID ? crypto.randomUUID() : _v4(options, buf, offset);
}
function _v4(options, buf, offset) {
	options ||= {};
	let rnds = options.random ?? options.rng?.() ?? rng();
	if (rnds.length < 16) throw Error("Random bytes length must be >= 16");
	if (rnds[6] = rnds[6] & 15 | 64, rnds[8] = rnds[8] & 63 | 128, buf) {
		if (offset ||= 0, offset < 0 || offset + 16 > buf.length) throw RangeError(`UUID byte range ${offset}:${offset + 15} is out of buffer bounds`);
		for (let i = 0; i < 16; ++i) buf[offset + i] = rnds[i];
		return buf;
	}
	return unsafeStringify(rnds);
}
function getRect(element) {
	let domRect = element.getBoundingClientRect();
	return {
		x: domRect.x + scrollX,
		y: domRect.y + scrollY,
		w: domRect.width,
		h: domRect.height
	};
}
function offsetRect(rect, px, axis) {
	return axis === "x" ? {
		x: rect.x + px,
		y: rect.y,
		w: rect.w - 2 * px,
		h: rect.h
	} : {
		x: rect.x,
		y: rect.y + px,
		w: rect.w,
		h: rect.h - 2 * px
	};
}
function rayIntersect(l1, l2) {
	let { x1, y1, x2, y2 } = l1, { x1: x3, y1: y3, x2: x4, y2: y4 } = l2;
	if (x1 === x2 && y1 === y2 || x3 === x4 && y3 === y4) return !1;
	let denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
	if (denominator === 0) return !1;
	let ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator, ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;
	return ua < 0 || ua > 1 || ub < 0 || ub > 1 ? !1 : {
		x: x1 + ua * (x2 - x1),
		y: y1 + ua * (y2 - y1)
	};
}
function rectEqual(r1, r2) {
	return r1.x === r2.x && r1.y === r2.y && r1.w === r2.w && r1.h === r2.h;
}
function rayRectIntersections(line, rect) {
	let rectLines = [
		{
			x1: rect.x,
			y1: rect.y,
			x2: rect.x + rect.w,
			y2: rect.y
		},
		{
			x1: rect.x + rect.w,
			y1: rect.y,
			x2: rect.x + rect.w,
			y2: rect.y + rect.h
		},
		{
			x1: rect.x + rect.w,
			y1: rect.y + rect.h,
			x2: rect.x,
			y2: rect.y + rect.h
		},
		{
			x1: rect.x,
			y1: rect.y + rect.h,
			x2: rect.x,
			y2: rect.y
		}
	], intersections = [];
	for (let i = 0; i < rectLines.length; i++) {
		let intersection = rayIntersect(line, rectLines[i]);
		if (intersection) {
			let isDuplicate = !1;
			for (let j = 0; j < intersections.length; j++) intersections[j].x === intersection.x && intersections[j].y === intersection.y && (isDuplicate = !0);
			isDuplicate || intersections.push(intersection);
		}
	}
	return intersections.length !== 0 && intersections.sort((a, b) => pointDist(a, {
		x: line.x1,
		y: line.y1
	}) - pointDist(b, {
		x: line.x1,
		y: line.y1
	}));
}
function pointDist(p1, p2) {
	let a = p1.x - p2.x, b = p1.y - p2.y;
	return Math.sqrt(a * a + b * b);
}
function pointInBounds(point, bounds) {
	let withinX = point.x >= bounds.x && point.x <= bounds.x + bounds.w, withinY = point.y >= bounds.y && point.y <= bounds.y + bounds.h;
	return withinX && withinY;
}
function findClosestIntersection(ray, targets, flow) {
	let rayOrigin = {
		x: ray.x1,
		y: ray.y1
	};
	if (targets.some((t) => pointInBounds(rayOrigin, offsetRect(t, Math.min(t.w, t.h) / 10, flow === "horizontal" ? "x" : "y")))) return null;
	let closestIntersection, closestRect;
	for (let target of targets) {
		let intersections = rayRectIntersections(ray, offsetRect(target, Math.min(target.w, target.h) / 10, flow === "horizontal" ? "x" : "y"));
		if (intersections) {
			let firstIntersection = intersections[0];
			closestIntersection ? pointDist(rayOrigin, firstIntersection) < pointDist(rayOrigin, closestIntersection) && (closestIntersection = firstIntersection, closestRect = target) : (closestIntersection = firstIntersection, closestRect = target);
		}
	}
	return closestRect || null;
}
function scaleRect(rect, scale, origin) {
	let { x, y, w, h } = rect, { x: originX, y: originY } = origin;
	return {
		x: originX + (x - originX) * scale,
		y: originY + (y - originY) * scale,
		w: w * scale,
		h: h * scale
	};
}
function getRectGroupXExtent(rects) {
	let minGroupX = Math.max(0, Math.min(...rects.map((r) => r.x))), maxGroupX = Math.min(document.body.offsetWidth, Math.max(...rects.map((r) => r.x + r.w)));
	return {
		min: minGroupX,
		max: maxGroupX,
		width: maxGroupX - minGroupX
	};
}
function getRectGroupYExtent(rects) {
	let minGroupY = Math.max(0, Math.min(...rects.map((r) => r.y))), maxGroupY = Math.min(document.body.scrollHeight, Math.max(...rects.map((r) => r.y + r.h)));
	return {
		min: minGroupY,
		max: maxGroupY,
		height: maxGroupY - minGroupY
	};
}
function calcTargetFlow(targets) {
	return targets.some((t1) => targets.filter((t2) => !rectEqual(t1, t2)).some((t2) => t1.y === t2.y)) ? "horizontal" : "vertical";
}
function calcInsertPosition(origin, targets, flow) {
	if (flow === "horizontal") {
		let rayLeft = {
			x1: origin.x,
			y1: origin.y,
			x2: origin.x - 1e8,
			y2: origin.y
		}, rayRight = {
			x1: origin.x,
			y1: origin.y,
			x2: origin.x + 1e8,
			y2: origin.y
		};
		return {
			left: findClosestIntersection(rayLeft, targets, flow),
			right: findClosestIntersection(rayRight, targets, flow)
		};
	}
	{
		let rayTop = {
			x1: origin.x,
			y1: origin.y,
			x2: origin.x,
			y2: origin.y - 1e8
		}, rayBottom = {
			x1: origin.x,
			y1: origin.y,
			x2: origin.x,
			y2: origin.y + 1e8
		};
		return {
			top: findClosestIntersection(rayTop, targets, flow),
			bottom: findClosestIntersection(rayBottom, targets, flow)
		};
	}
}
function findRectSanityData(rect, overlayGroup) {
	return overlayGroup.find((e) => rectEqual(getRect(e.elements.element), rect))?.sanity;
}
function resolveInsertPosition(overlayGroup, insertPosition, flow) {
	return Object.values(insertPosition).every((v) => v === null) ? null : flow === "horizontal" ? {
		left: insertPosition.left ? {
			rect: insertPosition.left,
			sanity: findRectSanityData(insertPosition.left, overlayGroup)
		} : null,
		right: insertPosition.right ? {
			rect: insertPosition.right,
			sanity: findRectSanityData(insertPosition.right, overlayGroup)
		} : null
	} : {
		top: insertPosition.top ? {
			rect: insertPosition.top,
			sanity: findRectSanityData(insertPosition.top, overlayGroup)
		} : null,
		bottom: insertPosition.bottom ? {
			rect: insertPosition.bottom,
			sanity: findRectSanityData(insertPosition.bottom, overlayGroup)
		} : null
	};
}
function calcMousePos(e) {
	let bodyBounds = document.body.getBoundingClientRect();
	return {
		x: Math.max(bodyBounds.x, Math.min(e.clientX, bodyBounds.x + bodyBounds.width)),
		y: e.clientY + window.scrollY
	};
}
function calcMousePosInverseTransform(mousePos) {
	let body = document.body, transform = window.getComputedStyle(body).transform;
	if (transform === "none") return {
		x: mousePos.x,
		y: mousePos.y
	};
	let inverseMatrix = new DOMMatrix(transform).inverse(), transformedPoint = new DOMPoint(mousePos.x, mousePos.y).matrixTransform(inverseMatrix);
	return {
		x: transformedPoint.x,
		y: transformedPoint.y
	};
}
function buildPreviewSkeleton(mousePos, element, scaleFactor) {
	let bounds = getRect(element), children = [...element.querySelectorAll(":where(h1, h2, h3, h4, p, a, img, span, button):not(:has(*))")];
	mousePos.x <= bounds.x && (mousePos.x = bounds.x), mousePos.x >= bounds.x + bounds.w && (mousePos.x = bounds.x + bounds.w), mousePos.y >= bounds.y + bounds.h && (mousePos.y = bounds.y + bounds.h), mousePos.y <= bounds.y && (mousePos.y = bounds.y);
	let childRects = children.map((child) => {
		let rect = scaleRect(getRect(child), scaleFactor, {
			x: bounds.x,
			y: bounds.y
		});
		return {
			x: rect.x - bounds.x,
			y: rect.y - bounds.y,
			w: rect.w,
			h: rect.h,
			tagName: child.tagName
		};
	});
	return {
		offsetX: (bounds.x - mousePos.x) * scaleFactor,
		offsetY: (bounds.y - mousePos.y) * scaleFactor,
		w: bounds.w * scaleFactor,
		h: bounds.h * scaleFactor,
		maxWidth: bounds.w * scaleFactor * .75,
		childRects
	};
}
async function applyMinimapWrapperTransform(target, scaleFactor, minYScaled, handler, rectUpdateFrequency) {
	return new Promise((resolve) => {
		target.addEventListener("transitionend", () => {
			setTimeout(() => {
				handler({ type: "overlay/dragEndMinimapTransition" });
			}, rectUpdateFrequency * 2), resolve();
		}, { once: !0 }), handler({ type: "overlay/dragStartMinimapTransition" }), handler({
			type: "overlay/dragToggleMinimap",
			display: !0
		}), document.body.style.overflow = "hidden", document.body.style.height = "100%", document.documentElement.style.overflow = "initial", document.documentElement.style.height = "100%", setTimeout(() => {
			target.style.transformOrigin = "50% 0px", target.style.transition = "transform 150ms ease", target.style.transform = `translate3d(0px, ${-minYScaled + scrollY}px, 0px) scale(${scaleFactor})`;
		}, 25);
	});
}
function calcMinimapTransformValues(rects, groupHeightOverride) {
	let groupHeight = groupHeightOverride || getRectGroupYExtent(rects).height;
	groupHeight += 200;
	let scaleFactor = groupHeight > window.innerHeight ? window.innerHeight / groupHeight : 1, { min: minYScaled } = getRectGroupYExtent(rects.map((r) => scaleRect(r, scaleFactor, {
		x: window.innerWidth / 2,
		y: 0
	})));
	return {
		scaleFactor,
		minYScaled: minYScaled - 100 * scaleFactor
	};
}
function calcGroupBoundsPreview(rects) {
	let groupBoundsX = getRectGroupXExtent(rects), groupBoundsY = getRectGroupYExtent(rects), canOffsetX = groupBoundsX.min > 8 && groupBoundsX.min + groupBoundsX.width <= window.innerWidth - 8, canOffsetY = groupBoundsY.min > 8 && groupBoundsY.min + groupBoundsY.height <= document.body.scrollHeight - 8, canOffset = canOffsetX && canOffsetY;
	return {
		x: canOffset ? groupBoundsX.min - 8 : groupBoundsX.min,
		y: canOffset ? groupBoundsY.min - 8 : groupBoundsY.min,
		w: canOffset ? groupBoundsX.width + 16 : groupBoundsX.width,
		h: canOffset ? groupBoundsY.height + 16 : groupBoundsY.height
	};
}
async function resetMinimapWrapperTransform(endYOrigin, target, prescaleHeight, handler, rectUpdateFrequency, previousRootStyleValues) {
	return new Promise((resolve) => {
		let transform = window.getComputedStyle(target).transform;
		if (new DOMMatrix(transform).a === 1) return;
		let maxScroll = prescaleHeight - window.innerHeight, prevScrollY = scrollY;
		endYOrigin -= window.innerHeight / 2, endYOrigin < 0 && (endYOrigin = 0), target.addEventListener("transitionend", () => {
			target.style.transition = "none", target.style.transform = "none", scrollTo({
				top: endYOrigin,
				behavior: "instant"
			}), setTimeout(() => {
				handler({ type: "overlay/dragEndMinimapTransition" }), handler({
					type: "overlay/dragToggleMinimap",
					display: !1
				});
			}, rectUpdateFrequency * 2), resolve();
		}, { once: !0 }), handler({ type: "overlay/dragStartMinimapTransition" }), target.style.transform = `translateY(${Math.max(prevScrollY - endYOrigin, -maxScroll + prevScrollY)}px) scale(1)`, previousRootStyleValues && (document.body.style.overflow = previousRootStyleValues.body.overflow, document.body.style.height = previousRootStyleValues.body.height, document.documentElement.style.overflow = previousRootStyleValues.documentElement.overflow, document.documentElement.style.height = previousRootStyleValues.documentElement.height);
	});
}
let minimapScaleApplied = !1, mousePosInverseTransform = {
	x: 0,
	y: 0
}, mousePos = {
	x: 0,
	y: 0
}, prescaleHeight = typeof document > "u" ? 0 : document.documentElement.scrollHeight, previousRootStyleValues = null;
function handleOverlayDrag(opts) {
	let { mouseEvent, element, overlayGroup, handler, target, onSequenceStart, onSequenceEnd } = opts;
	if (mouseEvent.button !== 0) return;
	window.focus();
	let rects = overlayGroup.map((e) => getRect(e.elements.element)), flow = element.getAttribute("data-sanity-drag-flow") || calcTargetFlow(rects), dragGroup = element.getAttribute("data-sanity-drag-group"), disableMinimap = !!element.getAttribute("data-sanity-drag-minimap-disable"), preventInsertDefault = !!element.getAttribute("data-sanity-drag-prevent-default"), documentHeightOverride = element.getAttribute("data-unstable_sanity-drag-document-height"), groupHeightOverride = element.getAttribute("data-unstable_sanity-drag-group-height"), insertPosition = null, initialMousePos = calcMousePos(mouseEvent), scaleTarget = document.body, { minYScaled, scaleFactor } = calcMinimapTransformValues(rects, groupHeightOverride ? ~~groupHeightOverride : null), sequenceStarted = !1, minimapPromptShown = !1, mousedown = !0;
	minimapScaleApplied || (previousRootStyleValues = {
		body: {
			overflow: window.getComputedStyle(document.body).overflow,
			height: window.getComputedStyle(document.body).height
		},
		documentElement: {
			overflow: window.getComputedStyle(document.documentElement).overflow,
			height: window.getComputedStyle(document.documentElement).height
		}
	}, prescaleHeight = documentHeightOverride ? ~~documentHeightOverride : document.documentElement.scrollHeight);
	let rectsInterval = setInterval(() => {
		rects = overlayGroup.map((e) => getRect(e.elements.element));
	}, 150), applyMinimap = () => {
		if (scaleFactor >= 1) return;
		let skeleton = buildPreviewSkeleton(mousePos, element, scaleFactor);
		handler({
			type: "overlay/dragUpdateSkeleton",
			skeleton
		}), handler({
			type: "overlay/dragToggleMinimapPrompt",
			display: !1
		}), applyMinimapWrapperTransform(scaleTarget, scaleFactor, minYScaled, handler, 150).then(() => {
			setTimeout(() => {
				handler({
					type: "overlay/dragUpdateGroupRect",
					groupRect: calcGroupBoundsPreview(rects)
				});
			}, 300);
		});
	}, handleScroll = (e) => {
		Math.abs(e.deltaY) >= 10 && scaleFactor < 1 && !minimapScaleApplied && !minimapPromptShown && !disableMinimap && mousedown && (handler({
			type: "overlay/dragToggleMinimapPrompt",
			display: !0
		}), minimapPromptShown = !0), e.shiftKey && !minimapScaleApplied && !disableMinimap && (window.dispatchEvent(new CustomEvent("unstable_sanity/dragApplyMinimap")), minimapScaleApplied = !0, setTimeout(() => {
			applyMinimap();
		}, 50));
	}, handleMouseMove = (e) => {
		if (e.preventDefault(), mousePos = calcMousePos(e), mousePosInverseTransform = calcMousePosInverseTransform(mousePos), Math.abs(pointDist(mousePos, initialMousePos)) < 4) return;
		if (!sequenceStarted) {
			let groupRect = calcGroupBoundsPreview(rects), skeleton = buildPreviewSkeleton(mousePos, element, 1);
			handler({
				type: "overlay/dragStart",
				flow
			}), handler({
				type: "overlay/dragUpdateSkeleton",
				skeleton
			}), handler({
				type: "overlay/dragUpdateGroupRect",
				groupRect
			}), sequenceStarted = !0, onSequenceStart();
		}
		handler({
			type: "overlay/dragUpdateCursorPosition",
			x: mousePos.x,
			y: mousePos.y
		}), e.shiftKey && !minimapScaleApplied && !disableMinimap && (window.dispatchEvent(new CustomEvent("unstable_sanity/dragApplyMinimap")), minimapScaleApplied = !0, setTimeout(() => {
			applyMinimap();
		}, 50));
		let newInsertPosition = calcInsertPosition(mousePos, rects, flow);
		JSON.stringify(insertPosition) !== JSON.stringify(newInsertPosition) && (insertPosition = newInsertPosition, handler({
			type: "overlay/dragUpdateInsertPosition",
			insertPosition: resolveInsertPosition(overlayGroup, insertPosition, flow)
		}));
	}, handleMouseUp = () => {
		mousedown = !1, handler({
			type: "overlay/dragEnd",
			target,
			insertPosition: insertPosition ? resolveInsertPosition(overlayGroup, insertPosition, flow) : null,
			dragGroup,
			flow,
			preventInsertDefault
		}), minimapPromptShown && handler({
			type: "overlay/dragToggleMinimapPrompt",
			display: !1
		}), minimapScaleApplied || (clearInterval(rectsInterval), onSequenceEnd(), removeFrameListeners(), removeKeyListeners()), removeMouseListeners();
	}, handleKeyup = (e) => {
		if (e.key === "Shift" && minimapScaleApplied) {
			minimapScaleApplied = !1;
			let skeleton = buildPreviewSkeleton(mousePos, element, 1 / scaleFactor);
			handler({
				type: "overlay/dragUpdateSkeleton",
				skeleton
			}), window.dispatchEvent(new CustomEvent("unstable_sanity/dragResetMinimap")), setTimeout(() => {
				resetMinimapWrapperTransform(mousePosInverseTransform.y, scaleTarget, prescaleHeight, handler, 150, previousRootStyleValues);
			}, 50), handler({
				type: "overlay/dragUpdateGroupRect",
				groupRect: null
			}), mousedown || (clearInterval(rectsInterval), removeMouseListeners(), removeFrameListeners(), removeKeyListeners(), onSequenceEnd());
		}
	}, handleBlur = () => {
		handler({
			type: "overlay/dragUpdateGroupRect",
			groupRect: null
		}), window.dispatchEvent(new CustomEvent("unstable_sanity/dragResetMinimap")), setTimeout(() => {
			resetMinimapWrapperTransform(mousePosInverseTransform.y, scaleTarget, prescaleHeight, handler, 150, previousRootStyleValues).then(() => {
				minimapScaleApplied = !1;
			});
		}, 50), clearInterval(rectsInterval), removeMouseListeners(), removeFrameListeners(), removeKeyListeners(), onSequenceEnd();
	}, removeMouseListeners = () => {
		window.removeEventListener("mousemove", handleMouseMove), window.removeEventListener("wheel", handleScroll), window.removeEventListener("mouseup", handleMouseUp);
	}, removeKeyListeners = () => {
		window.removeEventListener("keyup", handleKeyup);
	}, removeFrameListeners = () => {
		window.removeEventListener("blur", handleBlur);
	};
	window.addEventListener("blur", handleBlur), window.addEventListener("keyup", handleKeyup), window.addEventListener("wheel", handleScroll), window.addEventListener("mousemove", handleMouseMove), window.addEventListener("mouseup", handleMouseUp);
}
const isElementNode$1 = (target) => target instanceof HTMLElement || target instanceof SVGElement;
function findNonInlineElement(element) {
	let { display } = window.getComputedStyle(element);
	if (display !== "inline") return element;
	let parent = element.parentElement;
	return parent ? findNonInlineElement(parent) : null;
}
const findOverlayElement = (el) => !el || !isElementNode$1(el) ? null : el.dataset?.sanityOverlayElement ? el : findOverlayElement(el.parentElement);
/**
* JavaScript regexps are stateful. Have to reset lastIndex between runs to ensure consistent behaviour for the same string
* @param input
*/
function testVercelStegaRegex(input) {
	return VERCEL_STEGA_REGEX.lastIndex = 0, VERCEL_STEGA_REGEX.test(input);
}
/**
* Strips all stega-encoded metadata (sequences of invisible characters) from a string
* @param input
*/
function stegaClean(input) {
	return VERCEL_STEGA_REGEX.lastIndex = 0, input.replace(VERCEL_STEGA_REGEX, "");
}
function decodeStega(str, isAltText = !1) {
	try {
		let decoded = vercelStegaDecode(str);
		return !decoded || decoded.origin !== "sanity.io" ? null : (isAltText && (decoded.href = decoded.href?.replace(".alt", "")), decoded);
	} catch (err) {
		return console.error("Failed to decode stega for string: ", str, "with the original error: ", err), null;
	}
}
function testAndDecodeStega(str, isAltText = !1) {
	return testVercelStegaRegex(str) ? decodeStega(str, isAltText) : null;
}
const isElementNode = (node) => node.nodeType === Node.ELEMENT_NODE, isImgElement = (el) => el.tagName === "IMG", isTimeElement = (el) => el.tagName === "TIME", isSvgRootElement = (el) => el.tagName.toUpperCase() === "SVG";
function isSanityNode(node) {
	return "path" in node;
}
/**
* Finds commonality between two document paths strings
* @param first First path to compare
* @param second Second path to compare
* @returns A common path
*/
function findCommonPath(first, second) {
	let firstParts = first.split("."), secondParts = second.split("."), maxLength = Math.min(firstParts.length, secondParts.length);
	return firstParts = firstParts.slice(0, maxLength).reverse(), secondParts = secondParts.slice(0, maxLength).reverse(), firstParts.reduce((parts, part, i) => part === secondParts[i] ? [...parts, part] : [], []).reverse().join(".");
}
/**
* Returns common Sanity node data from multiple nodes
* If document paths are present, tries to resolve a common path
* @param nodes An array of Sanity nodes
* @returns A single sanity node or undefined
* @internal
*/
function findCommonSanityData(nodes) {
	if (!nodes.length || !nodes.map((n) => isSanityNode(n)).every((n, _i, arr) => n === arr[0])) return;
	if (!isSanityNode(nodes[0])) return nodes[0];
	let sanityNodes = nodes.filter(isSanityNode), common = nodes[0], consistentValueKeys = [
		"projectId",
		"dataset",
		"id",
		"baseUrl",
		"workspace",
		"tool"
	];
	for (let i = 1; i < sanityNodes.length; i++) {
		let node = sanityNodes[i];
		if (consistentValueKeys.some((key) => node[key] !== common?.[key])) {
			common = void 0;
			break;
		}
		common = {
			...common,
			path: findCommonPath(common.path, node.path)
		};
	}
	return common;
}
/**
* Finds nodes containing sanity specific data
* @param el - A parent element to traverse
* @returns An array of overlay targets
* @internal
*/
function findSanityNodes(el) {
	let mainResults = [];
	function createResolvedElement(element, data, reason, preventGrouping) {
		let sanity = decodeSanityNodeData(data);
		if (!sanity) return;
		let measureElement = findNonInlineElement(element);
		if (measureElement) return {
			elements: {
				element,
				measureElement
			},
			sanity,
			reason,
			preventGrouping
		};
	}
	function resolveNode(node) {
		let { nodeType, parentElement, textContent } = node;
		if (isElementNode(node) && node.dataset?.sanityEditTarget !== void 0) {
			let commonData = findCommonSanityData(findSanityNodes(node).map((node) => node.type === "element" ? node.commonSanity : void 0).filter((n) => n !== void 0));
			if (commonData) return {
				reason: "edit-target",
				elements: {
					element: node,
					measureElement: node
				},
				sanity: commonData
			};
		} else if (nodeType === Node.TEXT_NODE && parentElement && textContent) {
			let data = testAndDecodeStega(textContent);
			return data ? createResolvedElement(parentElement, data, "stega-text", !0) : void 0;
		} else if (isElementNode(node)) {
			if (node.tagName === "SCRIPT" || node.tagName === "SANITY-VISUAL-EDITING") return;
			if (node.dataset?.sanity) return createResolvedElement(node, node.dataset.sanity, "data-attribute", !!(node.textContent && testVercelStegaRegex(node.textContent)));
			if (node.dataset?.sanityEditInfo) return createResolvedElement(node, node.dataset.sanityEditInfo, "data-attribute", !!(node.textContent && testVercelStegaRegex(node.textContent)));
			if (isImgElement(node)) {
				let data = testAndDecodeStega(node.alt, !0);
				return data ? createResolvedElement(node, data, "stega-attribute") : void 0;
			}
			if (isTimeElement(node)) {
				let data = testAndDecodeStega(node.dateTime, !0);
				return data ? createResolvedElement(node, data, "stega-attribute") : void 0;
			}
			if (isSvgRootElement(node)) {
				if (!node.ariaLabel) return;
				let data = testAndDecodeStega(node.ariaLabel, !0);
				return data ? createResolvedElement(node, data, "stega-attribute") : void 0;
			}
		}
	}
	function processNode(node, _parentGroup) {
		let resolvedElement = resolveNode(node), parentGroup = _parentGroup;
		if (isElementNode(node) && node.dataset?.sanityEditGroup !== void 0 && (parentGroup = {
			type: "group",
			elements: {
				element: node,
				measureElement: node
			},
			targets: []
		}, mainResults.push(parentGroup)), resolvedElement) {
			let target = {
				elements: resolvedElement.elements,
				sanity: resolvedElement.sanity,
				reason: resolvedElement.reason
			};
			parentGroup && !resolvedElement.preventGrouping ? parentGroup.targets.push(target) : mainResults.push({
				elements: resolvedElement.elements,
				type: "element",
				targets: [target]
			});
		}
		if (isElementNode(node) && !isImgElement(node) && node.tagName !== "SCRIPT" && node.tagName !== "SANITY-VISUAL-EDITING") for (let childNode of node.childNodes) processNode(childNode, parentGroup);
	}
	if (el) for (let node of el.childNodes) processNode(node, void 0);
	return mainResults.map((node) => {
		if (node.targets.length === 0 && node.type === "group") return {
			...node,
			commonSanity: void 0
		};
		let commonSanity = node.targets.length === 1 ? node.targets[0].sanity : findCommonSanityData(node.targets.map(({ sanity }) => sanity).filter((n) => n !== void 0)) || node.targets[0].sanity;
		return commonSanity ? {
			...node,
			commonSanity
		} : null;
	}).filter((node) => node !== null);
}
function isSanityArrayPath(path) {
	let lastDotIndex = path.lastIndexOf(".");
	return path.substring(lastDotIndex, path.length).includes("[");
}
function getSanityNodeArrayPath(path) {
	if (!isSanityArrayPath(path)) return null;
	let split = path.split(".");
	return split[split.length - 1] = split[split.length - 1].replace(/\[.*?\]/g, "[]"), split.join(".");
}
function sanityNodesExistInSameArray(sanityNode1, sanityNode2) {
	return !isSanityArrayPath(sanityNode1.path) || !isSanityArrayPath(sanityNode2.path) ? !1 : getSanityNodeArrayPath(sanityNode1.path) === getSanityNodeArrayPath(sanityNode2.path);
}
function resolveDragAndDropGroup(element, sanity, elementSet, elementsMap) {
	if (!element.getAttribute("data-sanity") || element.getAttribute("data-sanity-drag-disable") || !sanity || !isSanityNode(sanity) || !isSanityArrayPath(sanity.path)) return null;
	let targetDragGroup = element.getAttribute("data-sanity-drag-group"), group = [...elementSet].reduce((acc, el) => {
		let elData = elementsMap.get(el), elDragDisabled = el.getAttribute("data-sanity-drag-disable"), elDragGroup = el.getAttribute("data-sanity-drag-group"), elHasSanityAttribution = el.getAttribute("data-sanity") !== null, sharedDragGroup = targetDragGroup === null || targetDragGroup === elDragGroup;
		return elData?.sanity && !elDragDisabled && isSanityNode(elData.sanity) && sanityNodesExistInSameArray(sanity, elData.sanity) && sharedDragGroup && elHasSanityAttribution && acc.push(elData), acc;
	}, []);
	return group.length <= 1 ? null : group;
}
/**
* Creates a controller which dispatches overlay related events
*
* @param handler - Dispatched event handler
* @param overlayElement - Parent element containing rendered overlay elements
* @public
*/
function createOverlayController({ handler, overlayElement, inFrame, inPopUp, optimisticActorReady }) {
	let activated = !1, elementIdMap = /* @__PURE__ */ new Map(), elementsMap = /* @__PURE__ */ new WeakMap(), elementSet = /* @__PURE__ */ new Set(), measureElements = /* @__PURE__ */ new WeakMap(), cursorMap = /* @__PURE__ */ new WeakMap(), ro, io, mo, activeDragSequence = !1, hoverStack = [], getHoveredElement = () => hoverStack[hoverStack.length - 1];
	function addEventHandlers(el, handlers) {
		el.addEventListener("click", handlers.click, { capture: !0 }), el.addEventListener("contextmenu", handlers.contextmenu, { capture: !0 }), el.addEventListener("mousemove", handlers.mousemove, {
			once: !0,
			capture: !0
		}), el.addEventListener("mousedown", handlers.mousedown, { capture: !0 });
	}
	function removeEventHandlers(el, handlers) {
		el.removeEventListener("click", handlers.click, { capture: !0 }), el.removeEventListener("contextmenu", handlers.contextmenu, { capture: !0 }), el.removeEventListener("mousemove", handlers.mousemove, { capture: !0 }), el.removeEventListener("mousedown", handlers.mousedown, { capture: !0 }), el.removeEventListener("mouseenter", handlers.mouseenter), el.removeEventListener("mouseleave", handlers.mouseleave);
	}
	/**
	* Executed when element enters the viewport
	* Enables an element’s event handlers
	*/
	function activateElement({ id, elements, handlers }) {
		let { element, measureElement } = elements;
		addEventHandlers(element, handlers), ro.observe(measureElement), handler({
			type: "element/activate",
			id
		});
	}
	/**
	* Executed when element leaves the viewport
	* Disables an element’s event handlers
	*/
	function deactivateElement({ id, elements, handlers }) {
		let { element, measureElement } = elements;
		removeEventHandlers(element, handlers), ro.unobserve(measureElement), hoverStack = hoverStack.filter((el) => el !== element), handler({
			type: "element/deactivate",
			id
		});
	}
	function setOverlayCursor(element) {
		if (!(!inFrame && !inPopUp || !optimisticActorReady)) for (let hoverstackElement of hoverStack) {
			if (element === hoverstackElement) {
				let targetSanityData = elementsMap.get(element)?.sanity;
				if (!targetSanityData || !isSanityNode(targetSanityData)) return;
				if (resolveDragAndDropGroup(element, targetSanityData, elementSet, elementsMap)) {
					let existingCursor = element.style.cursor;
					existingCursor && cursorMap.set(element, existingCursor), handler({
						type: "overlay/setCursor",
						element,
						cursor: "move"
					});
					continue;
				}
			}
			restoreOverlayCursor(hoverstackElement);
		}
	}
	function restoreOverlayCursor(element) {
		handler({
			type: "overlay/setCursor",
			element,
			cursor: cursorMap.get(element)
		});
	}
	/**
	* Stores an element’s DOM node and decoded sanity data in state and sets up event handlers
	*/
	function registerElement({ type, elements, commonSanity, targets }) {
		let { element, measureElement } = elements, eventHandlers = {
			click(event) {
				let target = event.target;
				if (element === getHoveredElement() && element.contains(target)) {
					inFrame && (event.preventDefault(), event.stopPropagation());
					let sanity = elementsMap.get(element)?.sanity;
					sanity && !activeDragSequence && handler({
						type: "element/click",
						id,
						sanity
					});
				}
			},
			contextmenu(event) {
				if (!("path" in commonSanity) || !inFrame && !inPopUp || !optimisticActorReady || !commonSanity.path.split(".").pop()?.includes("[_key==")) return;
				let target = event.target;
				element === getHoveredElement() && element.contains(target) && ((inFrame || inPopUp) && (event.preventDefault(), event.stopPropagation()), handler({
					type: "element/contextmenu",
					id,
					position: {
						x: event.clientX,
						y: event.clientY
					},
					sanity: commonSanity
				}));
			},
			mousedown(event) {
				if (event.preventDefault(), event.currentTarget !== hoverStack.at(-1) || element.getAttribute("data-sanity-drag-disable") || !inFrame && !inPopUp || !optimisticActorReady) return;
				let targetSanityData = elementsMap.get(element)?.sanity;
				if (!targetSanityData || !isSanityNode(targetSanityData) || !isSanityArrayPath(targetSanityData.path)) return;
				let dragGroup = resolveDragAndDropGroup(element, commonSanity, elementSet, elementsMap);
				dragGroup && handleOverlayDrag({
					element,
					handler,
					mouseEvent: event,
					overlayGroup: dragGroup,
					target: targetSanityData,
					onSequenceStart: () => {
						activeDragSequence = !0;
					},
					onSequenceEnd: () => {
						setTimeout(() => {
							activeDragSequence = !1;
						}, 250);
					}
				});
			},
			mousemove(event) {
				eventHandlers.mouseenter(event);
				let el = event.currentTarget;
				el && (el.addEventListener("mouseenter", eventHandlers.mouseenter), el.addEventListener("mouseleave", eventHandlers.mouseleave));
			},
			mouseenter() {
				document.querySelector("vercel-live-feedback") && element.closest("[data-vercel-edit-info]") || element.closest("[data-vercel-edit-target]") || (hoverStack.push(element), handler({
					type: "element/mouseenter",
					id,
					rect: getRect(element)
				}), setOverlayCursor(element));
			},
			mouseleave(e) {
				function leave() {
					hoverStack.pop();
					let hoveredElement = getHoveredElement();
					if (handler({
						type: "element/mouseleave",
						id
					}), hoveredElement) {
						setOverlayCursor(hoveredElement);
						let overlayElement = elementsMap.get(hoveredElement);
						overlayElement && handler({
							type: "element/mouseenter",
							id: overlayElement.id,
							rect: getRect(hoveredElement)
						});
					}
					restoreOverlayCursor(element);
				}
				/**
				* If moving to an element within the overlay which handles pointer events, attach a new
				* event handler to that element and defer the original leave event
				*/
				function addDeferredLeave(el) {
					let deferredLeave = (e) => {
						let { relatedTarget } = e;
						findOverlayElement(relatedTarget) ? relatedTarget && isElementNode$1(relatedTarget) && (el.removeEventListener("mouseleave", deferredLeave), addDeferredLeave(relatedTarget)) : (el.removeEventListener("mouseleave", deferredLeave), leave());
					};
					el.addEventListener("mouseleave", deferredLeave);
				}
				let { relatedTarget } = e, container = findOverlayElement(relatedTarget), isInteractiveOverlayElement = overlayElement.contains(container);
				if (isElementNode$1(container) && isInteractiveOverlayElement) return addDeferredLeave(container);
				leave();
			}
		}, id = v4(), sanityNode = {
			type,
			id,
			elements,
			sanity: commonSanity,
			handlers: eventHandlers
		};
		elementSet.add(element), measureElements.set(measureElement, element), elementIdMap.set(id, element), elementsMap.set(element, sanityNode), io?.observe(element), handler({
			type: "element/register",
			elementType: type,
			id,
			element,
			rect: getRect(element),
			sanity: commonSanity,
			dragDisabled: !!element.getAttribute("data-sanity-drag-disable"),
			targets: targets.map((target) => ({
				sanity: target.sanity,
				element: target.elements.element
			}))
		}), activated && activateElement(sanityNode);
	}
	function updateElement(resolvedElement) {
		let { element } = resolvedElement.elements, overlayElement = elementsMap.get(element);
		overlayElement && (elementsMap.set(element, {
			...overlayElement,
			sanity: resolvedElement.commonSanity
		}), handler({
			type: "element/update",
			elementType: overlayElement.type,
			id: overlayElement.id,
			rect: getRect(element),
			sanity: resolvedElement.commonSanity,
			targets: resolvedElement.targets.map((target) => ({
				sanity: target.sanity,
				element: target.elements.element
			}))
		}));
	}
	function parseElements(node) {
		let sanityNodes = findSanityNodes(node);
		for (let sanityNode of sanityNodes) {
			if (sanityNode.type === "group") {
				for (let target of sanityNode.targets) {
					let overlayElement = elementsMap.get(target.elements.element);
					overlayElement && overlayElement.type === "element" && unregisterElement(target.elements.element);
				}
				sanityNode.targets.length === 0 && unregisterElement(sanityNode.elements.element);
			}
			if (!sanityNode.commonSanity) continue;
			let { element } = sanityNode.elements;
			elementsMap.has(element) ? updateElement(sanityNode) : registerElement(sanityNode);
		}
	}
	function unregisterElement(element) {
		let overlayElement = elementsMap.get(element);
		if (overlayElement) {
			let { id, handlers } = overlayElement;
			removeEventHandlers(element, handlers), ro.unobserve(element), elementsMap.delete(element), elementSet.delete(element), elementIdMap.delete(id), handler({
				type: "element/unregister",
				id
			});
		}
	}
	function handleMutation(mutations) {
		let mutationWasInScope = !1;
		for (let mutation of mutations) {
			let { target, type } = mutation, node = type === "characterData" ? target.parentElement : target;
			if (!(node === overlayElement || overlayElement.contains(node)) && (mutationWasInScope = !0, isElementNode$1(node))) {
				let possibleGroupParent = node.parentElement?.closest("[data-sanity-edit-group]") || null;
				parseElements({ childNodes: [isElementNode$1(possibleGroupParent) ? possibleGroupParent : node] });
			}
		}
		if (mutationWasInScope) for (let element of elementSet) element.isConnected || unregisterElement(element), elementsMap.get(element)?.type === "group" && !element.hasAttribute("data-sanity-edit-group") && unregisterElement(element);
	}
	function updateRect(el) {
		let overlayElement = elementsMap.get(el);
		overlayElement && handler({
			type: "element/updateRect",
			id: overlayElement.id,
			rect: getRect(el)
		});
	}
	function handleResize(entries) {
		for (let entry of entries) {
			let target = entry.target;
			if (isElementNode$1(target)) {
				let element = measureElements.get(target);
				if (!element) return;
				updateRect(element);
			}
		}
	}
	function handleIntersection(entries) {
		if (activated) for (let entry of entries) {
			let { target } = entry, match = isElementNode$1(target) && elementsMap.get(target);
			match && (entry.isIntersecting ? activateElement(match) : deactivateElement(match));
		}
	}
	function handleBlur(event) {
		let element = findOverlayElement(event.target);
		if (element) {
			element.dataset.sanityOverlayElement === "capture" && (event.preventDefault(), event.stopPropagation());
			return;
		}
		hoverStack = [], handler({ type: "overlay/blur" });
	}
	function handleExclusivePluginClosed() {
		hoverStack = [], handler({ type: "overlay/reset-mouse-state" });
	}
	function handleWindowResize() {
		for (let element of elementSet) updateRect(element);
	}
	function handleKeydown(event) {
		event.key === "Escape" && (hoverStack = [], handler({ type: "overlay/blur" }));
	}
	function handleWindowScroll(event) {
		let { target } = event;
		if (!(target === window.document || !isElementNode$1(target))) for (let element of elementSet) target.contains(element) && updateRect(element);
	}
	function activate() {
		activated ||= (io = new IntersectionObserver(handleIntersection, { threshold: .3 }), elementSet.forEach((element) => io.observe(element)), handler({ type: "overlay/activate" }), !0);
	}
	function deactivate() {
		activated &&= (io?.disconnect(), elementSet.forEach((element) => {
			let overlayElement = elementsMap.get(element);
			overlayElement && deactivateElement(overlayElement);
		}), handler({ type: "overlay/deactivate" }), !1);
	}
	function handleHeaderClick(event) {
		let { id } = event.detail, element = elementIdMap.get(id);
		if (!element) return;
		let sanity = elementsMap.get(element)?.sanity;
		sanity && handler({
			type: "element/click",
			id,
			sanity
		});
	}
	function destroy() {
		window.removeEventListener("click", handleBlur), window.removeEventListener("contextmenu", handleBlur), window.removeEventListener("sanity-overlay/exclusive-plugin-closed", handleExclusivePluginClosed), window.removeEventListener("sanity-overlay/label-click", handleHeaderClick), window.removeEventListener("keydown", handleKeydown), window.removeEventListener("resize", handleWindowResize), window.removeEventListener("scroll", handleWindowScroll), mo.disconnect(), ro.disconnect(), elementSet.forEach((element) => {
			unregisterElement(element);
		}), elementIdMap.clear(), elementSet.clear(), hoverStack = [], deactivate();
	}
	function create() {
		window.addEventListener("click", handleBlur), window.addEventListener("contextmenu", handleBlur), window.addEventListener("sanity-overlay/exclusive-plugin-closed", handleExclusivePluginClosed), window.addEventListener("sanity-overlay/label-click", handleHeaderClick), window.addEventListener("keydown", handleKeydown), window.addEventListener("resize", handleWindowResize), window.addEventListener("scroll", handleWindowScroll, {
			capture: !0,
			passive: !0
		}), ro = new ResizeObserver(handleResize), mo = new MutationObserver(handleMutation), mo.observe(document.body, {
			attributes: !0,
			characterData: !0,
			childList: !0,
			subtree: !0
		}), parseElements(document.body), activate();
	}
	return window.document.fonts.ready.then(() => {
		for (let element of elementSet) updateRect(element);
	}), create(), {
		activate,
		deactivate,
		destroy
	};
}
const SharedStateContext = createContext(null);
export { testVercelStegaRegex as a, stegaClean as i, createOverlayController as n, v4 as o, sanityNodesExistInSameArray as r, SharedStateContext as t };

//# sourceMappingURL=SharedStateContext-DPUAUle3.js.map